import { useEffect, useSyncExternalStore } from 'react';
import type { VersionInfo } from '@tg/shared';
import { api } from '@/api/client';

/** This front bundle (vite.config.ts `define`, scripts/build-info.mjs). */
export const FRONT_BUILD: Readonly<typeof __TG_BUILD__> = __TG_BUILD__;

/**
 * GET /api/version, once per page load and shared by every footer. Four explicit states — silence would be a fifth
 * one («nobody asked»): `loading`, `ok`, `error` («version not read»), plus `idle` before the first footer mounts.
 * A failed read is retried by the next footer that mounts (navigation), not in a loop.
 */
export type ServerVersionState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ok'; info: VersionInfo }
  | { status: 'error' };

let state: ServerVersionState = { status: 'idle' };
const listeners = new Set<() => void>();

function set(next: ServerVersionState): void {
  state = next;
  for (const l of listeners) l();
}

export function loadServerVersion(): void {
  if (state.status === 'loading' || state.status === 'ok') return;
  set({ status: 'loading' });
  api.get<VersionInfo>('/api/version').then(
    (info) => set(info && typeof info.version === 'string' ? { status: 'ok', info } : { status: 'error' }),
    () => set({ status: 'error' }),
  );
}

/** tests: forget the cached answer */
export function resetServerVersion(): void {
  set({ status: 'idle' });
}

export function useServerVersion(): ServerVersionState {
  const s = useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => state,
  );
  useEffect(() => loadServerVersion(), []);
  return s;
}

/**
 * The browser holds an older front than the server runs: both sides know their commit and they differ.
 * In development the server has no build-info (commit null) — nothing to compare, no warning.
 */
export function isStaleFront(server: VersionInfo, front: { commit: string | null } = FRONT_BUILD): boolean {
  return !!server.commit && !!front.commit && server.commit !== front.commit;
}
